import * as React from 'react';
import { Popover } from '@mui/material';
import { useAppTranslation } from '../../services/i18n';

/** Apple keyboards label the modifier ⌘, everything else Ctrl -- both trigger the same actions */
export const isAppleKeyboard = (): boolean =>
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/i.test(navigator.userAgent);

interface Props {
  anchorEl: HTMLElement | null;
  /** off drops the keys that only mean something inside a tree */
  isTreeEnabled: boolean;
  onClose: () => void;
}

export const WorkplaceShortcuts: React.FC<Props> = ({ anchorEl, isTreeEnabled, onClose }) => {
  const t = useAppTranslation();
  const mod = isAppleKeyboard() ? '⌘' : 'Ctrl';

  // A flat list has nothing to collapse, nest or move under, and the keys for those are
  // inert there -- listing them would be teaching shortcuts that do nothing.
  const groups: { title: string; rows: { keys: string; label: string }[] }[] = [
    {
      title: t('workplaces.shortcuts.navigation'),
      rows: [
        { keys: '↑ ↓', label: t('workplaces.shortcuts.move') },
        ...(isTreeEnabled
          ? [{ keys: '← →', label: t('workplaces.shortcuts.collapseExpand') }]
          : []),
        { keys: 'Home / End', label: t('workplaces.shortcuts.homeEnd') },
        { keys: 'A–Z', label: t('workplaces.shortcuts.typeahead') },
      ],
    },
    {
      title: t('workplaces.shortcuts.editing'),
      rows: [
        { keys: 'Enter', label: t('workplaces.shortcuts.open') },
        {
          keys: mod + ' ↵',
          label: t(
            isTreeEnabled ? 'workplaces.shortcuts.newSibling' : 'workplaces.shortcuts.newFlat'
          ),
        },
        ...(isTreeEnabled
          ? [
              { keys: mod + ' ⇧ ↵', label: t('workplaces.shortcuts.newChild') },
              { keys: 'Alt ← →', label: t('workplaces.shortcuts.indent') },
            ]
          : []),
        { keys: 'Alt ↑ ↓', label: t('workplaces.shortcuts.reorder') },
        { keys: 'Delete', label: t('workplaces.shortcuts.delete') },
        {
          keys: 'Esc',
          label: t(
            isTreeEnabled ? 'workplaces.shortcuts.escape' : 'workplaces.shortcuts.escapeFlat'
          ),
        },
      ],
    },
  ];

  return (
    <Popover
      open={!!anchorEl}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      slotProps={{ paper: { className: 'workplace-shortcuts' } }}
    >
      <div className={'workplace-shortcuts__head'}>{t('workplaces.shortcuts.title')}</div>
      <div className={'workplace-shortcuts__body'}>
        {groups.map((group) => (
          <React.Fragment key={group.title}>
            <div className={'workplace-shortcuts__group'}>{group.title}</div>
            {group.rows.map((row) => (
              <div key={row.keys} className={'workplace-shortcuts__row'}>
                <span className={'workplace-shortcuts__keys'}>{row.keys}</span>
                <span className={'workplace-shortcuts__label'}>{row.label}</span>
              </div>
            ))}
          </React.Fragment>
        ))}
        <p className={'workplace-shortcuts__note'}>
          {t(isTreeEnabled ? 'workplaces.shortcuts.note' : 'workplaces.shortcuts.noteFlat')}
        </p>
      </div>
    </Popover>
  );
};
